import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/site/Reveal";
import SectionHead from "@/components/site/SectionHead";
import CTASection from "@/components/site/CTASection";
import { useLang } from "@/i18n";

const CASE_IMAGES = {
  tykun: "/images/bluewake-trophy.webp",
  sanya: "/images/sanya-cover.png",
};
const TYKUN_GALLERY = [
  { src: "/images/monaco-1.webp", alt: "Il tender Tykun X ormeggiato al Monaco Yacht Show con le bandiere del brand" },
  { src: "/images/monaco-harbour.webp", alt: "Il porto di Monaco al tramonto — Monaco Yacht Show" },
  { src: "/images/monaco-3.webp", alt: "Lo stand Tykun al Monaco Yacht Show con i materiali Blue Wake" },
  { src: "/images/monaco-2.webp", alt: "I tender Tykun esposti con il totem delle specifiche tecniche" },
  { src: "/images/tykun-team.webp", alt: "Il team Tykun al lavoro sul progetto" },
  { src: "/images/monaco-4.webp", alt: "Il porto di Monaco visto dall'acqua durante lo Yacht Show" },
  { src: "/images/tesya-presentation-1.webp", alt: "La presentazione ufficiale Tykun/H1 all'evento Tesya Caterpillar — slide Project H1" },
  { src: "/images/tesya-presentation-2.webp", alt: "La presentazione ufficiale Tykun/H1 all'evento Tesya Caterpillar — studi tecnici" },
];

const CaseBlock = ({ c, idx, labels }) => (
  <section className={`py-20 lg:py-28 ${idx % 2 === 0 ? "border-t border-ink/10 bg-white" : "bg-paper"}`} data-testid={`case-${c.id}`}>
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <div className="grid items-start gap-12 lg:grid-cols-12">
        <Reveal className={`lg:col-span-6 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
          <div className="relative overflow-hidden">
            <img src={CASE_IMAGES[c.id]} alt={c.imgAlt} loading="lazy" className="aspect-[4/3] w-full object-cover" data-testid={`case-${c.id}-image`} />
            {c.badge && (
              <span className="absolute left-4 top-4 bg-accent px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white" data-testid={`case-${c.id}-badge`}>
                {c.badge}
              </span>
            )}
          </div>
        </Reveal>
        <div className={`lg:col-span-6 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{c.overline}</p>
            <h2 className="mt-4 font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl">{c.title}</h2>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{c.role}</p>
            {c.meta && (
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink/70" data-testid={`case-${c.id}-meta`}>{c.meta}</p>
            )}
            <p className="mt-6 text-base leading-relaxed text-mutedink">{c.intro}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-ink">{labels.activitiesLabel}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {c.activities.map((a) => (
                <span key={a} className="border border-ink/15 bg-paper px-3 py-1.5 text-xs text-ink/80">
                  {a}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-8 border-l-2 border-accent pl-5 font-serif text-xl font-light italic leading-snug text-ink" data-testid={`case-${c.id}-outcome`}>
              {c.outcome}
            </p>
          </Reveal>
          {c.quote && (
            <Reveal delay={0.24}>
              <figure className="mt-8 border border-ink/12 bg-white p-7" data-testid={`case-${c.id}-quote`}>
                <blockquote className="font-serif text-lg font-light italic leading-relaxed text-ink">
                  {c.quote.text}
                </blockquote>
                <figcaption className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-accent">
                  {c.quote.author}
                </figcaption>
              </figure>
            </Reveal>
          )}
          {c.sites && c.sites.length > 0 && (
            <Reveal delay={0.3}>
              <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-ink">{labels.sitesLabel}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                {c.sites.map((s, si) => (
                  <a
                    key={s.url}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`case-${c.id}-site-${si}`}
                    className="inline-flex items-center gap-2 border border-ink/25 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:border-accent hover:text-accent"
                  >
                    <ArrowUpRight className="h-4 w-4 text-accent" /> {s.label}
                  </a>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </div>
      {c.id === "tykun" && (
        <Reveal delay={0.1} className="mt-14">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{labels.galleryLabel}</p>
          <div className="-mx-6 mt-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 sm:-mx-8 sm:px-8 lg:-mx-12 lg:px-12" data-testid="case-tykun-gallery">
            {TYKUN_GALLERY.map((g, i) => (
              <div key={g.src} className="group w-72 shrink-0 snap-center overflow-hidden sm:w-96">
                <img src={g.src} alt={g.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" data-testid={`case-tykun-gallery-${i}`} />
              </div>
            ))}
          </div>
          {c.galleryNote && <p className="mt-3 text-xs text-mutedink" data-testid="case-tykun-gallery-note">{c.galleryNote}</p>}
        </Reveal>
      )}
      {c.video && (
        <Reveal delay={0.15} className="mt-14">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{c.video.label}</p>
          <div className="mt-5 max-w-sm overflow-hidden border border-ink/10 bg-ink">
            <video controls preload="none" poster="/videos/teaser-tykun-poster.webp" className="aspect-[9/16] w-full" data-testid={`case-${c.id}-video`}>
              <source src="/videos/teaser-tykun.mp4" type="video/mp4" />
            </video>
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-accent" data-testid={`case-${c.id}-video-note`}>{c.video.note}</p>
        </Reveal>
      )}
    </div>
  </section>
);

const CaseStudy = () => {
  const { d } = useLang();
  const p = d.cs;
  const labels = { activitiesLabel: p.activitiesLabel, galleryLabel: p.galleryLabel, sitesLabel: p.sitesLabel };
  return (
    <div data-testid="case-study-page">
      <section className="bg-paper py-20 lg:py-28" data-testid="cs-hero">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-accent"
          >
            <span className="h-px w-10 bg-accent" /> {p.heroLabel}
          </motion.p>
          <h1 className="max-w-4xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="cs-headline">
            <MaskedLine delay={0.1}>{p.heroL1}</MaskedLine>
            <MaskedLine delay={0.25}><span className="italic text-accent">{p.heroL2}</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-mutedink sm:text-lg"
          >
            {p.sub}
          </motion.p>
        </div>
      </section>

      {p.cases.slice(0, 1).map((c, idx) => (
        <CaseBlock key={c.id} c={c} idx={idx} labels={labels} />
      ))}

      {p.more && (
        <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="more-projects-section">
          <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
            <SectionHead overline={p.more.overline} title={p.more.title} />
            <div className="mt-14 grid gap-8 lg:grid-cols-2">
              {p.more.cards.map((m, i) => (
                <Reveal key={m.title} delay={i * 0.1} className="flex flex-col border border-ink/12 bg-paper p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent lg:p-10" data-testid={`more-card-${i}`}>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">{m.role}</p>
                  <h3 className="mt-3 font-serif text-3xl font-light text-ink">{m.title}</h3>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink">{m.meta}</p>
                  <p className="mt-5 flex-1 text-sm leading-relaxed text-mutedink">{m.body}</p>
                  {m.brands && m.brands.length > 0 && (
                    <div className="mt-6 border-t border-ink/10 pt-5">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink">{p.more.brandsLabel}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {m.brands.map((b) => (
                          <span key={b} className="border border-ink/15 bg-white px-3 py-1.5 text-xs text-ink/80">
                            {b}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {m.sites && m.sites.length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {m.sites.map((s, si) => (
                        <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`more-card-${i}-site-${si}`} className="inline-flex items-center gap-1.5 border border-ink/20 bg-white px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/80 transition-colors duration-300 hover:border-accent hover:text-accent">
                          <ArrowUpRight className="h-3 w-3 text-accent" /> {s.label}
                        </a>
                      ))}
                    </div>
                  )}
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {p.cases.slice(1).map((c, idx) => (
        <CaseBlock key={c.id} c={c} idx={idx + 1} labels={labels} />
      ))}

      <CTASection />
    </div>
  );
};

export default CaseStudy;
